import { Directive, Input, Signal, computed, inject } from '@angular/core';
import { IConfigDictionary } from '@partners/api';
import { TConfigPartition } from '@partners/types';
import { FeatureConfig } from '../../../config';

/**
 * @description
 * This directive is a BASE directive (i.e. meant to be inherited only). It provides an api to read feature's config values,
 * such as texts, indications, links & params. All that data comes from the server and saved in the store. The directive's
 * inheritors are specialized directives (see IndicationConfigDirective, TextConfigDirective) which consume indications and
 * texts data and provides it to the template.
 */

@Directive({
  standalone: true
})
export class FeatureConfigDirective {
  @Input()
  featureConfig: string;

  @Input()
  dictionary: 'feature' | 'global' = 'feature';

  /** we need the feature's name to read config data from the store */
  get currentConfig$$(): Signal<IConfigDictionary> {
    return this._featureConfig.configDictionary$$;
  }

  private _featureConfig = inject(FeatureConfig);

  constructor() {}

  /** read a boolean indication from config node  */
  protected fromIndications$$(key: string, value: boolean = true): Signal<boolean> {
    return computed(() => {
      const { indication } = { ...this.currentConfig$$() };
      /** Added specific scenario for checking false indications,
       *  if the key is absent indFalseConfig should returns true.*/
      if (value === false && !(key in indication)) {
        return true;
      }
      return this.fromConfig(indication, key) === value;
    });
  }
  /** read text from config node  */
  protected fromText$$(key: string): Signal<string> {
    const { text } = { ...this.currentConfig$$() };
    return computed(() => this.fromConfig(text, key));
  }
  /** read a link from config node  */
  protected fromLinks$$(key: string): Signal<string> {
    const { links } = { ...this.currentConfig$$() };
    return computed(() => this.fromConfig(links, key));
  }
  /** read param object from config node  */
  protected fromParams$$(key: string): Signal<object> {
    const { params } = { ...this.currentConfig$$() };
    return computed(() => this.fromConfig(params, key));
  }

  private fromConfig<T>(dictionary: TConfigPartition<unknown>, key: string): T {
    if (dictionary && key in dictionary) {
      return dictionary[key] as T;
    }
    return null;
  }
}
